import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight, ChevronDown, Building2, Users, PauseCircle, TrendingUp } from 'lucide-react';
import { useApi } from '../../../lib/api';
import PartnersSubNav from './PartnersSubNav';
import ReasonModal from '../../../components/shared/ReasonModal';
import ConfirmDeleteModal from '../../../components/workspace/ConfirmDeleteModal';
import DonutChart from '../components/DonutChart';
import { sliceColor, UNASSIGNED_COLOR } from '../components/tierColors';
import '../../../styles/Dashboard.css';
import '../../../styles/workspace.css';
import '../../../styles/PartnerConsole.css';
import Pagination from '../../../components/shared/Pagination';
import { SearchableSelect } from '../../../components/workspace/filters/FilterDropdowns';

const PAGE_SIZE = 10;

const STATUS_COLORS = {
  active:    { bg: '#eafaf0', fg: '#16a34a' },
  suspended: { bg: '#fdecea', fg: '#b42318' },
};

const CLIENT_STATUS_LABELS = {
  unlinked: 'Waiting for signup',
  pending: 'Pending approval',
  active: 'Active',
  declined: 'Declined',
  removed: 'Removed by client',
  revoked: 'Revoked',
};

const CLIENT_STATUS_COLORS = {
  unlinked: { bg: '#f3f4f6', fg: '#6b7280' },
  pending:  { bg: '#fff4e5', fg: '#b45309' },
  active:   { bg: '#eafaf0', fg: '#16a34a' },
  declined: { bg: '#fce7f3', fg: '#be185d' },
  removed:  { bg: '#f3e8ff', fg: '#7e22ce' },
  revoked:  { bg: '#fdecea', fg: '#b42318' },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: '#f3f4f6', fg: '#6b7280' };
  return (
    <span className="pc-pill" style={{ background: c.bg, color: c.fg }}>
      {status}
    </span>
  );
}

function ClientStatusBadge({ status }) {
  const c = CLIENT_STATUS_COLORS[status] || CLIENT_STATUS_COLORS.unlinked;
  return (
    <span className="pc-pill" style={{ background: c.bg, color: c.fg }}>
      {CLIENT_STATUS_LABELS[status] || status}
    </span>
  );
}

export default function PartnersListPage() {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suspendId, setSuspendId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedClients, setExpandedClients] = useState({});
  const [expandLoading, setExpandLoading] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [tierChange, setTierChange] = useState(null); // { partner, tierId }

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [pRes, tRes] = await Promise.all([api.adminListPartners(), api.adminListPartnerTiers()]);
      setItems(pRes.items || []);
      setTiers(tRes.items || []);
      setExpandedClients({});
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleReactivate = async (partnerId) => {
    setBusyId(partnerId);
    try {
      await api.adminSetPartnerStatus(partnerId, 'active');
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const toggleExpand = async (partnerId) => {
    if (expandedId === partnerId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(partnerId);
    if (!expandedClients[partnerId]) {
      setExpandLoading(true);
      try {
        const res = await api.adminListPartnerClientLinks({ partner_id: partnerId });
        setExpandedClients((prev) => ({ ...prev, [partnerId]: res.items || [] }));
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setExpandLoading(false);
      }
    }
  };

  // Throws on failure so the confirm dialog can show the server error.
  const handleApproveUpgrade = async () => {
    await api.adminApprovePartnerUpgrade(upgradeTarget.id, upgradeTarget.eligible_tier.id);
    refresh();
  };

  const handleSuspend = async (reason) => {
    setBusyId(suspendId);
    try {
      await api.adminSetPartnerStatus(suspendId, 'suspended', reason);
      setSuspendId(null);
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Partners</h1>
          <p className="admin-page-subtitle">Approved partners, tier assignment, and status.</p>
        </div>
      </div>
      <PartnersSubNav />

      {items.length > 0 && (() => {
        const activeCount = items.filter((it) => it.status === 'active').length;
        const suspendedCount = items.filter((it) => it.status === 'suspended').length;
        const activeClients = items.reduce((s, it) => s + (it.active_client_count || 0), 0);
        const eligibleCount = items.filter((it) => it.eligible_tier).length;
        const tierCounts = {};
        for (const it of items) {
          const key = it.tier_name || 'Not Assigned';
          tierCounts[key] = (tierCounts[key] || 0) + 1;
        }
        let colorIdx = 0;
        const tierSlices = Object.entries(tierCounts).map(([label, value]) => ({
          label,
          value,
          color: label === 'Not Assigned' ? UNASSIGNED_COLOR : sliceColor(colorIdx++),
        }));

        return (
          <>
            <div className="admin-kpi-row">
              <div className="admin-kpi">
                <div className="admin-kpi-icon blue"><Building2 size={18} /></div>
                <p className="admin-kpi-label">Total Partners</p>
                <p className="admin-kpi-value">{items.length}</p>
              </div>
              <div className="admin-kpi ok">
                <div className="admin-kpi-icon green"><Users size={18} /></div>
                <p className="admin-kpi-label">Active</p>
                <p className="admin-kpi-value">{activeCount}</p>
              </div>
              <div className="admin-kpi">
                <div className="admin-kpi-icon red"><PauseCircle size={18} /></div>
                <p className="admin-kpi-label">Suspended</p>
                <p className="admin-kpi-value">{suspendedCount}</p>
              </div>
              <div className="admin-kpi">
                <div className="admin-kpi-icon purple"><Users size={18} /></div>
                <p className="admin-kpi-label">Active Clients</p>
                <p className="admin-kpi-value">{activeClients}</p>
              </div>
              <div
                className={`admin-kpi ${eligibleCount > 0 ? 'warn' : ''}`}
                style={{ cursor: eligibleCount > 0 ? 'pointer' : undefined }}
                onClick={() => { if (eligibleCount > 0) { setStatusFilter('eligible'); setPage(1); } }}
                title={eligibleCount > 0 ? 'Show partners eligible for an upgrade' : undefined}
              >
                <div className="admin-kpi-icon orange"><TrendingUp size={18} /></div>
                <p className="admin-kpi-label">Eligible for Upgrade</p>
                <p className="admin-kpi-value">{eligibleCount}</p>
              </div>
            </div>

            <div className="admin-card">
              <h2 style={{ marginBottom: 16 }}>Partners by tier</h2>
              <div className="admin-donut-wrap">
                <DonutChart slices={tierSlices} size={110} />
                <div className="admin-donut-legend">
                  {tierSlices.map((sl) => {
                    const pct = Math.round((sl.value / items.length) * 100);
                    return (
                      <div key={sl.label} className="admin-donut-legend-item">
                        <div className="admin-donut-legend-label">
                          <div className="admin-donut-dot" style={{ background: sl.color }} />
                          {sl.label}
                        </div>
                        <div>
                          <span className="admin-donut-value">{sl.value}</span>
                          <span className="admin-donut-pct">({pct}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        );
      })()}

      <div className="admin-card-toolbar" style={{ justifyContent: 'flex-end' }}>
        <SearchableSelect
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'active', label: 'Active' },
            { value: 'suspended', label: 'Suspended' },
            { value: 'eligible', label: 'Eligible for upgrade' },
          ]}
          showAll={false}
          btnClass="form-select-btn"
          wrapStyle={{ width: 220 }}
          matchTriggerWidth
        />
      </div>

      {error && <div className="admin-banner error">{error}</div>}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">No partners yet.</p>
      ) : (() => {
        const filtered = statusFilter === 'eligible'
          ? items.filter((it) => it.eligible_tier)
          : statusFilter ? items.filter((it) => it.status === statusFilter) : items;
        const pagedItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        return filtered.length === 0 ? (
          <p className="muted">{statusFilter === 'eligible' ? 'No partners are eligible for an upgrade right now.' : `No ${statusFilter} partners.`}</p>
        ) : (
        <>
        <div className="entity-table-wrap">
          <table className="entity-table" style={{ width: '100%', minWidth: 860 }}>
            <colgroup>
              <col style={{ width: '26%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: 110 }} />
              <col />
              <col style={{ width: 120 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Company</th>
                <th>Status</th>
                <th style={{ width: 120 }} title="Active / total client links">Clients</th>
                <th>Tier</th>
                <th className="row-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedItems.map((it) => (
                <React.Fragment key={it.id}>
                  <tr>
                    <td style={{ overflowWrap: 'anywhere' }}>{it.company_name}<div className="muted" style={{ fontSize: 12 }}>{it.user_email}</div></td>
                    <td>
                      <StatusBadge status={it.status} />
                      {it.status === 'suspended' && it.suspension_reason && (
                        <div className="muted" style={{ fontSize: 12, marginTop: 4, overflowWrap: 'anywhere' }}>{it.suspension_reason}</div>
                      )}
                    </td>
                    <td>
                      {it.client_count > 0 ? (
                        <button
                          type="button"
                          className="btn-link-sm"
                          onClick={() => toggleExpand(it.id)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          {expandedId === it.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <span title={`${it.active_client_count || 0} active of ${it.client_count} total`}>
                            {it.active_client_count || 0} / {it.client_count}
                          </span>
                        </button>
                      ) : (
                        0
                      )}
                    </td>
                    <td>
                      <SearchableSelect
                        value={it.tier_id || ''}
                        onChange={(v) => { if (String(v) !== String(it.tier_id || '')) setTierChange({ partner: it, tierId: v }); }}
                        options={[
                          { value: '', label: 'Not Assigned' },
                          // Only active tiers are assignable; keep the partner's current
                          // tier visible (marked Inactive) if it has since been deactivated.
                          ...tiers
                            .filter((t) => t.is_active || String(t.id) === String(it.tier_id))
                            .map((t) => ({
                              value: String(t.id),
                              label: `${t.name} (${t.commission_percentage}%)${t.is_active ? '' : ' — Inactive'}`,
                            })),
                        ]}
                        showAll={false}
                        btnClass="form-select-btn"
                        disabled={busyId === it.id}
                        matchTriggerWidth
                      />
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        {it.paying_client_count || 0} paying client{(it.paying_client_count || 0) === 1 ? '' : 's'}
                      </div>
                      {it.eligible_tier && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                          <span className="pc-pill" style={{ background: '#fff4e5', color: '#b45309', whiteSpace: 'normal', maxWidth: '100%', overflowWrap: 'anywhere' }}>
                            Eligible for {it.eligible_tier.name}
                          </span>
                          <button className="btn-link-sm" onClick={() => setUpgradeTarget(it)} disabled={busyId === it.id}>
                            Approve upgrade
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="row-actions">
                      {it.status === 'active' ? (
                        <button className="btn-link-sm" onClick={() => setSuspendId(it.id)} disabled={busyId === it.id}>Suspend</button>
                      ) : (
                        <button className="btn-link-sm activate" onClick={() => handleReactivate(it.id)} disabled={busyId === it.id}>Reactivate</button>
                      )}
                    </td>
                  </tr>
                  {expandedId === it.id && (
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--bg-muted, #f9fafb)', padding: '10px 16px' }}>
                        {expandLoading && !expandedClients[it.id] ? (
                          <span className="muted" style={{ fontSize: 13 }}>Loading clients…</span>
                        ) : (expandedClients[it.id] || []).length === 0 ? (
                          <span className="muted" style={{ fontSize: 13 }}>No clients found.</span>
                        ) : (
                          <div className="pc-scroll-y" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, paddingRight: 4 }}>
                            {expandedClients[it.id].map((c) => (
                              <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                <div>
                                  <span style={{ fontWeight: 600, fontSize: 13 }}>{c.client_name || c.client_email}</span>
                                  {c.client_name && (
                                    <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>{c.client_email}</span>
                                  )}
                                </div>
                                <ClientStatusBadge status={c.status} />
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalItems={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
        );
      })()}

      {(() => {
        const t = tierChange && tiers.find((x) => String(x.id) === String(tierChange.tierId));
        return (
          <ConfirmDeleteModal
            open={!!tierChange}
            title={tierChange ? `Change tier for ${tierChange.partner.company_name}?` : ''}
            message={tierChange
              ? (t
                  ? `Move from ${tierChange.partner.tier_name || 'Not Assigned'} to ${t.name} (${t.commission_percentage}%). The new rate applies to future client payments only.`
                  : `Remove the tier from this partner. They will not earn commission until a tier is assigned again.`)
              : ''}
            confirmLabel="Change Tier"
            onConfirm={async () => {
              await api.adminAssignPartnerTier(tierChange.partner.id, tierChange.tierId ? Number(tierChange.tierId) : null);
              refresh();
            }}
            onClose={() => setTierChange(null)}
          />
        );
      })()}

      <ConfirmDeleteModal
        open={!!upgradeTarget}
        title={upgradeTarget ? `Upgrade ${upgradeTarget.company_name} to ${upgradeTarget.eligible_tier.name}?` : ''}
        message={upgradeTarget
          ? `They have ${upgradeTarget.paying_client_count} paying client${upgradeTarget.paying_client_count === 1 ? '' : 's'}, meeting the ${upgradeTarget.eligible_tier.name} target of ${upgradeTarget.eligible_tier.target_clients}. `
            + `The new ${upgradeTarget.eligible_tier.commission_percentage}% rate applies to future client payments only; the partner is emailed.`
          : ''}
        confirmLabel="Approve Upgrade"
        onConfirm={handleApproveUpgrade}
        onClose={() => setUpgradeTarget(null)}
      />

      {suspendId != null && (
        <ReasonModal
          title="Suspend Partner"
          label="Reason for suspension"
          confirmLabel="Confirm Suspend"
          busy={busyId === suspendId}
          onConfirm={handleSuspend}
          onClose={() => setSuspendId(null)}
        />
      )}
    </div>
  );
}
